import { FloatingFormItem, Group, InputDecimal } from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import { useWorkOrderCommissionsByEmployeer } from '@/core/domain/workorder/WorkOrdersCommisions/WorkOrdersCommissions.hooks';
import {
  AddManyWorkOrderCommissionState,
  WorkOrderCommission,
} from '@/core/domain/workorder/WorkOrdersCommisions/WorkOrdersCommissions.types';
import {
  WorkOrderItemType,
  workorderTypeColor,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useDisclosure } from '@/hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { COLUMNS_WIDTH } from '@/utils/table';
import { Button, Checkbox, Form, FormInstance, Statistic, Table } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPercent } from 'react-icons/fa';
import CommissionListModal from '../../Items/Commission/CommissionListModal';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useEmployee } from '@/core/domain/enterprise/Employees/Employees.hooks';
import Authorities from '@/types/Authority';
import { CommissionFormFilter } from '../CommissionsItens';
import formatterPrice from '@/utils/number/formatterPrice';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useColorScheme } from '@/contexts/ColorSchemeContextProvider';
import { usePartStockPricesMutation } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { useTaskMutation } from '@/core/domain/operational/Tasks/Tasks.hooks';

interface Props {
  formFilter: FormInstance<CommissionFormFilter>;
}

const CommissionsItensList: React.FC<Props> = ({ formFilter }) => {
  const form = Form.useFormInstance<AddManyWorkOrderCommissionState>();

  const [commissionSelected, setCommission] = useState<WorkOrderCommission>();
  const [commissionModalOpen, commissionModalAction] = useDisclosure(false, {
    onClose: () => setCommission(undefined),
  });

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const employeeId = Form.useWatch('employeeId', formFilter);
  const type = Form.useWatch('itemType', formFilter);

  const formItems = Form.useWatch('items', form);

  const totalBaseValue = useMemo(() => {
    return formItems?.reduce((acc, cur) => acc + (cur?.baseValue || 0), 0) || 0;
  }, [formItems]);

  const totalCommissionBaseValue = useMemo(() => {
    return (
      formItems?.reduce(
        (acc, cur) => acc + (cur?.commissionBaseValue || 0),
        0,
      ) || 0
    );
  }, [formItems]);

  const [colorScheme] = useColorScheme();

  const [commissionItems, setCommissionItems] = useState<WorkOrderCommission[]>(
    [],
  );

  const { data, isLoading, dataUpdatedAt } = useWorkOrderCommissionsByEmployeer(
    {
      complementId: complement?.id,
      type,
      employeeId,
    },
  );

  const commissionsData = flatInfiniteData(data);

  const { data: employee } = useEmployee(employeeId);

  const partStockPrices = usePartStockPricesMutation();

  const taskDetail = useTaskMutation();

  const getPartCommission = async (partId: number | null) => {
    if (!partId) return null;

    const stockPriceList = await partStockPrices.mutateAsync(partId);
    const stockPrices = stockPriceList.result.find(
      (stock) => stock.company.id === workorder?.company.id,
    );
    return (stockPrices?.commissionPercentage || 0) * 100;
  };

  const getTaskCommission = async (taskId: number | null) => {
    if (!taskId) return null;

    const taskDetailData = await taskDetail.mutateAsync(taskId);
    const taskPrices = taskDetailData.taskPriceSet.find(
      (stock) => stock.companyId === workorder?.company.id,
    );
    return taskPrices?.commissionPercentage || 0;
  };

  const getPercentConfig = async (item: WorkOrderCommission) => {
    switch (item.type) {
      case WorkOrderItemType.Part:
        const partCommission = await getPartCommission(item.partId);
        return (
          partCommission ||
          employee?.professionalInformation?.partCommission ||
          0
        );
      case WorkOrderItemType.Service:
        const taskCommission = await getTaskCommission(item.taskId);
        return (
          taskCommission ||
          employee?.professionalInformation?.serviceCommission ||
          0
        );
      default:
        return undefined;
    }
  };

  const checkedFormItems = useMemo(() => {
    return formItems?.filter((item) => item?.checked) ?? [];
  }, [formItems]);

  const selectedRowKeys = useMemo(() => {
    return checkedFormItems.map((formItem) => formItem.itemId) || [];
  }, [checkedFormItems]);

  useEffect(() => {
    if (commissionsData.length) {
      setCommissionItems(
        commissionsData.map((item) => {
          if (item.haveCommission) {
            return {
              ...item,
              checked: true,
            };
          } else {
            return {
              ...item,
              commissionQuantity: undefined,
              commissionBaseValue: undefined,
              percent: undefined,
              baseValue: undefined,
              checked: false,
            };
          }
        }) as WorkOrderCommission[],
      );
    } else {
      setCommissionItems([]);
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    if (commissionItems.length) {
      form?.setFieldValue(
        'items',
        commissionItems.map((item) => ({
          ...item,
          checked: !!item.haveCommission,
        })),
      );
    } else {
      form?.setFieldValue('items', []);
    }
  }, [commissionItems]);

  const isChecked = useCallback(
    (itemId: number) => selectedRowKeys.includes(itemId),
    [selectedRowKeys],
  );

  async function onCheckItem(keys: React.Key[]) {
    const previousKeys = selectedRowKeys;

    const numericKeys = keys.map((key) => Number(key));
    const added = numericKeys.filter((key) => !previousKeys.includes(key));
    const removed = previousKeys.filter((key) => !numericKeys.includes(key));

    if (!added.length && !removed.length) return;

    const currentItems = form.getFieldValue('items') || [];

    const updatedItems = [...currentItems];

    removed.forEach((key) => {
      const index = updatedItems.findIndex((item) => item.itemId === key);
      if (index !== -1) {
        updatedItems[index] = {
          ...updatedItems[index],
          checked: false,
          commissionQuantity: undefined,
          commissionBaseValue: undefined,
          percent: undefined,
          baseValue: undefined,
        };
      }
    });

    const itemsToAdd = added
      .map((key) => updatedItems.find((item) => item.itemId === key))
      .filter(Boolean);

    const results = await Promise.all(
      itemsToAdd.map(async (item) => {
        let percent = item.percent ?? 0;

        if (!percent) {
          percent = (await getPercentConfig(item)) ?? 0;
        }

        return {
          ...item,
          checked: true,
          commissionQuantity: item.quantity,
          commissionBaseValue: (item.total * percent) / 100,
          percent,
          baseValue: item.total,
        };
      }),
    );

    results.forEach((updated) => {
      const index = updatedItems.findIndex(
        (item) => item.itemId === updated.itemId,
      );
      if (index !== -1) {
        updatedItems[index] = updated;
      }
    });

    if (updatedItems.some((item) => !!item.itemId)) {
      form?.setFieldsValue({ items: updatedItems, employeeId });
    } else {
      form?.setFieldsValue({ items: [], employeeId: undefined });
    }

    await form?.validateFields(['employeeId']);
  }

  function isRegistre(itemId: number) {
    return commissionItems.some(
      (item) => item.itemId === itemId && item.haveCommission,
    );
  }

  function calculatePercent(baseValue?: number, commissionValue?: number) {
    if (!baseValue || !commissionValue) return 0;
    return (commissionValue / baseValue) * 100;
  }

  function calculateCommissionValue(baseValue?: number, percent?: number) {
    if (!baseValue || !percent) return 0;
    return (baseValue * percent) / 100;
  }

  const columns: ColumnsType<WorkOrderCommission> = [
    {
      title: 'Cód. Fabricante',
      dataIndex: 'manufacturingCode',
      width: COLUMNS_WIDTH.default,
      render: (code, _, index) => (
        <>
          <Form.Item hidden name={[index, 'itemId']} />
          {code || '-'}
        </>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'type',
      width: COLUMNS_WIDTH.default,
      render: (_, item) =>
        item.type === WorkOrderItemType.Part ||
        item.type === WorkOrderItemType.Service ? (
          <StatusTag
            color={workorderTypeColor[item.type]}
            label={item.type === WorkOrderItemType.Part ? 'Peça' : 'Serviço'}
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'Descrição',
      dataIndex: 'itemName',
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
      render: (description, _, index) => (
        <>
          <Form.Item
            hidden
            name={[index, 'checked']}
            valuePropName="checked"
            noStyle
          >
            <Checkbox />
          </Form.Item>
          {description}
        </>
      ),
    },
    {
      title: 'Quantidade',
      dataIndex: 'quantity',
      width: COLUMNS_WIDTH.default,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      align: 'right',
      width: COLUMNS_WIDTH.price,
      ellipsis: true,
      render: (total) => formatterPrice(total),
    },
    {
      title: 'Qtd. Comissão',
      dataIndex: 'commissionQuantity',
      render: (_, row, index) => (
        <FloatingFormItem
          name={[index, 'commissionQuantity']}
          fieldKey={[index, 'commissionQuantity']}
          parentFieldName="items"
          label="Qtd. Comissão"
          initialValue={row.haveCommission ? row.commissionQuantity : undefined}
        >
          <InputDecimal
            min={0}
            max={row.quantity}
            disabled={!isChecked(row.itemId) && !row.haveCommission}
          />
        </FloatingFormItem>
      ),
      width: COLUMNS_WIDTH.default,
    },
    {
      title: 'Comissão %',
      dataIndex: 'percent',
      render: (_, row, index) => (
        <FloatingFormItem
          name={[index, 'percent']}
          fieldKey={[index, 'percent']}
          parentFieldName="items"
          label="Comissão %"
        >
          <InputDecimal
            min={0}
            max={100}
            disabled={!isChecked(row.itemId) && !row.haveCommission}
            onChange={(percent) => {
              const baseValue: number =
                form.getFieldValue(['items', index, 'baseValue']) || 0;
              const commissionBaseValue = calculateCommissionValue(
                baseValue,
                percent || 0,
              );

              form.setFieldValue(
                ['items', index, 'commissionBaseValue'],
                commissionBaseValue,
              );
            }}
          />
        </FloatingFormItem>
      ),
      width: COLUMNS_WIDTH.default,
    },
    {
      title: 'Comissão $',
      dataIndex: 'commissionBaseValue',
      render: (_, row, index) => (
        <FloatingFormItem
          name={[index, 'commissionBaseValue']}
          fieldKey={[index, 'commissionBaseValue']}
          parentFieldName="items"
          label="Comissão $"
        >
          <InputDecimal
            min={0}
            disabled={!isChecked(row.itemId) && !row.haveCommission}
            onChange={(commissionBaseValue) => {
              const baseValue: number =
                form.getFieldValue(['items', index, 'baseValue']) || 0;

              const percent = calculatePercent(
                baseValue,
                commissionBaseValue || 0,
              );

              form.setFieldValue(['items', index, 'percent'], percent);
            }}
          />
        </FloatingFormItem>
      ),
      width: COLUMNS_WIDTH.default,
    },
    {
      title: 'Valor Base',
      dataIndex: 'baseValue',
      render: (_, row, index) => (
        <FloatingFormItem
          name={[index, 'baseValue']}
          fieldKey={[index, 'baseValue']}
          parentFieldName="items"
          label="Valor base"
          initialValue={row.haveCommission ? row.baseValue : undefined}
        >
          <InputDecimal
            min={0}
            max={row.total}
            disabled={
              (!isChecked(row.itemId) && !row.haveCommission) ||
              !hasPermissions([
                Authorities.MOVIMENTATION_WORKORDER_CHANGE_BASE_COMMISSION_VALUE,
              ])
            }
            onChange={(baseValue) => {
              const percent: number =
                form.getFieldValue(['items', index, 'percent']) || 0;
              const commissionBaseValue = calculateCommissionValue(
                baseValue || 0,
                percent,
              );

              form.setFieldValue(
                ['items', index, 'commissionBaseValue'],
                commissionBaseValue,
              );
            }}
          />
        </FloatingFormItem>
      ),
      width: COLUMNS_WIDTH.default,
    },
    {
      title: '',
      dataIndex: 'haveCommission',
      width: COLUMNS_WIDTH.small,
      render: (haveCommission, row) =>
        haveCommission ? (
          <Button
            type="link"
            icon={<FaPercent color={'#2ac72a'} />}
            size="small"
            onClick={() => {
              setCommission(row);
              commissionModalAction.open();
            }}
          />
        ) : null,
    },
  ];

  return (
    <>
      <Form.List initialValue={[]} name="items" key={dataUpdatedAt}>
        {() => {
          return (
            <Table
              pagination={false}
              loading={isLoading || partStockPrices.isLoading}
              columns={columns}
              rowKey={(row) => row.itemId}
              dataSource={commissionItems || []}
              scroll={{ x: true }}
              rowSelection={{
                selectedRowKeys,
                type: 'checkbox',
                onChange: onCheckItem,
                getCheckboxProps: (row) => {
                  const partStockLoading =
                    partStockPrices.isLoading &&
                    partStockPrices.variables === row.partId;
                  return {
                    disabled: isRegistre(row.itemId) || partStockLoading,
                  };
                },
              }}
            />
          );
        }}
      </Form.List>

      {hasPermissions([
        Authorities.VIEW_TOTAL_BASE_VALUE_AND_TOTAL_COMISSION_IN_BUDGET,
      ]) && (
        <Group gap={10} align="end" position="right">
          <Statistic
            title="Total commissão"
            valueRender={() => formatterPrice(totalCommissionBaseValue || 0)}
            valueStyle={{
              color: colorScheme === 'light' ? '#364068' : '#fff',
            }}
            style={{
              whiteSpace: 'nowrap',
            }}
          />

          <Statistic
            title="Total valor base"
            valueRender={() => formatterPrice(totalBaseValue || 0)}
            valueStyle={{
              color: colorScheme === 'light' ? '#364068' : '#fff',
            }}
            style={{
              whiteSpace: 'nowrap',
            }}
          />
        </Group>
      )}

      <CommissionListModal
        open={commissionModalOpen}
        onCancel={() => {
          commissionModalAction.close();
        }}
        onlyList
        itemId={commissionSelected?.itemId}
        quantity={commissionSelected?.quantity}
        onRemoveSuccess={() => null}
      />
    </>
  );
};

export default CommissionsItensList;
